import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { GuidedTour } from "@components/common/GuidedTour";
import { steps } from "@components/FirstTimeUserTour/steps";
import { POPULATE_USER_SAMPLE_TEMPLATES } from "@graphql/mutations/PopulateUserSampleTemplates";
import { usePreferences, useTemplates } from "@hooks";
import { usePremiumFeature } from "@hooks/use-premium-feature";
import { useTour } from "@hooks/use-tour";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useEffect, useMemo, useRef } from "react";
import { ACTIONS } from "react-joyride";
import { useNavigate } from "react-router";

export const FirstTimeUserTour = () => {
  const { firstTimeTourDisabled, setFirstTimeTourDisabled } =
    usePreferences();
  const { refetchTemplates } = useTemplates();
  const [populateSampleTemplates] = useMutation(
    POPULATE_USER_SAMPLE_TEMPLATES,
  );
  const isResettingRef = useRef(false);
  const navigate = useNavigate();
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();
  const hasAdaptiveTemplates = hasFeatureAccess(
    UserPermissions.ADAPTIVE_TEMPLATES_ENABLED,
  );
  const { releaseWebTemplateSelectionDropdown } = useFlags();

  const activeSteps = useMemo(
    () =>
      steps.filter(
        (s) =>
          (!s.requiresAdaptiveTemplates || hasAdaptiveTemplates) &&
          (!s.requiresTemplateDropdown ||
            releaseWebTemplateSelectionDropdown) &&
          (!s.requiresTemplateCardGrid ||
            !releaseWebTemplateSelectionDropdown),
      ),
    [hasAdaptiveTemplates, releaseWebTemplateSelectionDropdown],
  );

  const { run, setRun, stepIndex, setStepIndex, handleCallback } =
    useTour({
      name: "First Time User Tour",
      steps: activeSteps,
      isActive: firstTimeTourDisabled === false,
      onComplete: () => setFirstTimeTourDisabled(true),
      pollForStart: false,
      onStepAfter: (index, action, { setRun, setStepIndex }) => {
        if (
          action === ACTIONS.NEXT &&
          activeSteps[index].title === "Review Your Note" &&
          activeSteps[index].target === "body"
        ) {
          setRun(false);
          setStepIndex(index + 1);
          if (hasAdaptiveTemplates) {
            populateSampleTemplates().then(() => refetchTemplates());
          } else {
            navigate("/dashboard");
          }
          return true;
        }
      },
    });

  // Complex per-step polling: start tour, eager-skip, abort, etc.
  useEffect(() => {
    if (firstTimeTourDisabled !== false) {
      return;
    }

    const currentStep = activeSteps[stepIndex];
    const nextStep = activeSteps[stepIndex + 1];
    let numIterations = 0;
    const interval = setInterval(() => {
      if (!run && document.querySelector(currentStep.target)) {
        if (stepIndex === 0) {
          mixpanel.track("First Time User Tour", {
            eventType: "Started",
          });
        }
        isResettingRef.current = false;
        setRun(true);
        return;
      }
      if (
        currentStep.waitIterations &&
        numIterations <= currentStep.waitIterations
      ) {
        numIterations += 1;
        return;
      }
      if (
        currentStep.abortTarget &&
        document.querySelector(currentStep.abortTarget)
      ) {
        mixpanel.track("First Time User Tour", {
          eventType: "Aborted",
          stepIndex,
          isAutomatic: true,
        });
        alert("error", currentStep.abortMessage);
        isResettingRef.current = true;
        clearInterval(interval);
        setRun(false);
        setStepIndex(0);
      } else if (
        (currentStep.eagerSkip &&
          document.querySelector(nextStep.target)) ||
        (currentStep.skipOnTargetLost &&
          !document.querySelector(currentStep.target))
      ) {
        mixpanel.track("First Time User Tour", {
          eventType: "Advanced",
          stepIndex,
          isAutomatic: true,
        });
        clearInterval(interval);
        setRun(false);
        setStepIndex(stepIndex + 1);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [
    stepIndex,
    firstTimeTourDisabled,
    run,
    activeSteps,
    setRun,
    setStepIndex,
  ]);

  const wrappedCallback = (data) => {
    if (isResettingRef.current) return;
    handleCallback(data);
  };

  return (
    <GuidedTour
      steps={activeSteps}
      run={run}
      stepIndex={stepIndex}
      onEvent={wrappedCallback}
      locale={{ last: "Finish" }}
    />
  );
};
